import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Card,
    CardContent,
    useMediaQuery,
    useTheme,
    IconButton,
    Fade,
    Zoom,
    Slide,
    Grow,
    LinearProgress,
    Chip,
} from '@mui/material';
import { useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import LightModeIcon from '@mui/icons-material/LightMode';
import Brightness3Icon from '@mui/icons-material/Brightness3';
import StarIcon from '@mui/icons-material/Star';
import FavoriteIcon from '@mui/icons-material/Favorite';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SchoolIcon from '@mui/icons-material/School';
import Api from '../../ajax';
import './WelcomeScreen.css';

const InitialWelcomeScreen = ({ open, onContinue, onCancel }) => {
    const currentUser = useSelector((state) => state.currentUser.value);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [showContent, setShowContent] = useState(false);
    const [particles, setParticles] = useState([]);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [showWelcomeAnimation, setShowWelcomeAnimation] = useState(false);
    const [institutionData, setInstitutionData] = useState(null);
    const api = new Api();

    // Generate floating particles
    const generateParticles = useCallback(() => {
        const newParticles = [];
        for (let i = 0; i < (isMobile ? 15 : 25); i++) {
            newParticles.push({
                id: i,
                x: Math.random() * 100,
                y: Math.random() * 100,
                size: Math.random() * 8 + 4,
                delay: Math.random() * 5,
                duration: Math.random() * 10 + 8,
                opacity: Math.random() * 0.6 + 0.2,
            });
        }
        return newParticles;
    }, [isMobile]);

    useEffect(() => {
        if (open) {
            setParticles(generateParticles());
            setShowContent(true);
            
            // Fetch institution data
            api.post("/institution/get-by-id", {}, (res) => {
                if (res && res.inst_name) {
                    setInstitutionData(res);
                }
            });
            
            // Simulate loading progress
            const progressInterval = setInterval(() => {
                setLoadingProgress(prev => {
                    if (prev >= 100) {
                        clearInterval(progressInterval);
                        setTimeout(() => setShowWelcomeAnimation(true), 300);
                        return 100;
                    }
                    return prev + Math.random() * 15 + 5;
                });
            }, 200);

            // Auto-close after 6 seconds removed - let user control when to proceed
            // const autoCloseTimer = setTimeout(() => {
            //     if (onContinue) {
            //         onContinue();
            //     }
            // }, 6000);

            return () => {
                clearInterval(progressInterval);
                // clearTimeout(autoCloseTimer);
            };
        } else {
            setShowContent(false);
            setLoadingProgress(0);
            setShowWelcomeAnimation(false);
            setInstitutionData(null);
        }
    }, [open, generateParticles, onContinue]);

    // Get time-based greeting with enhanced styling
    const getTimeBasedGreeting = () => {
        const hour = new Date().getHours();
        const date = new Date();
        const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'long' });

        if (hour >= 5 && hour < 12) {
            return {
                greeting: 'Good Morning',
                icon: WbSunnyIcon,
                gradient: 'linear-gradient(135deg, #FFD93D 0%, #FF6B35 50%, #F7931E 100%)',
                cardGradient: 'linear-gradient(135deg, rgba(255, 217, 61, 0.15) 0%, rgba(255, 107, 53, 0.15) 100%)',
                iconColor: '#FFD93D',
                accentColor: '#FF6B35',
                emojis: ['☀️', '🌅', '🌞', '⭐', '✨'],
                message: `Happy ${dayOfWeek}! Ready to make today amazing? Let's dive into your personalized learning journey.`,
                mood: 'energetic',
                particles: 25
            };
        } else if (hour >= 12 && hour < 17) {
            return {
                greeting: 'Good Afternoon',
                icon: LightModeIcon,
                gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                cardGradient: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                iconColor: '#667eea',
                accentColor: '#764ba2',
                emojis: ['🌤️', '☁️', '🌈', '⚡', '💫'],
                message: `Wonderful ${dayOfWeek} afternoon! Hope your day is flowing smoothly. Check out what's new and exciting.`,
                mood: 'calm',
                particles: 20
            };
        } else {
            return {
                greeting: 'Good Evening',
                icon: Brightness3Icon,
                gradient: 'linear-gradient(135deg, #2C3E50 0%, #4A569D 50%, #6366f1 100%)',
                cardGradient: 'linear-gradient(135deg, rgba(44, 62, 80, 0.15) 0%, rgba(99, 102, 241, 0.15) 100%)',
                iconColor: '#6366f1',
                accentColor: '#4A569D',
                emojis: ['🌙', '⭐', '🌟', '💫', '✨'],
                message: `Peaceful ${dayOfWeek} evening! Time to unwind and catch up on today's highlights and achievements.`,
                mood: 'serene',
                particles: 18
            };
        }
    };

    const timeInfo = getTimeBasedGreeting();
    const GreetingIcon = timeInfo.icon;

    if (!open) return null;

    return (
        <Dialog
            open={open}
            maxWidth={isMobile ? 'xs' : 'sm'}
            fullWidth
            fullScreen={isMobile}
            TransitionComponent={Zoom}
            transitionDuration={800}
            PaperProps={{
                style: {
                    borderRadius: isMobile ? 0 : 24,
                    background: `
                        linear-gradient(135deg, rgba(248, 250, 252, 0.9) 0%, rgba(241, 245, 249, 0.8) 50%, rgba(236, 242, 249, 0.9) 100%),
                        linear-gradient(45deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.06) 50%, rgba(59, 130, 246, 0.08) 100%)
                    `,
                    color: '#1e293b',
                    overflow: 'hidden',
                    boxShadow: `
                        0 20px 60px rgba(0, 0, 0, 0.1),
                        0 8px 32px rgba(59, 130, 246, 0.08),
                        inset 0 1px 0 rgba(255, 255, 255, 0.8)
                    `,
                    backdropFilter: 'blur(24px) saturate(120%)',
                    WebkitBackdropFilter: 'blur(24px) saturate(120%)',
                    border: '1px solid rgba(255, 255, 255, 0.6)',
                },
            }}
            BackdropProps={{
                style: {
                    backgroundColor: 'rgba(15, 23, 42, 0.6)',
                    backdropFilter: 'blur(16px) saturate(110%)',
                    WebkitBackdropFilter: 'blur(16px) saturate(110%)',
                },
            }}
        >
            {/* Animated Particle Background */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    overflow: 'hidden',
                    pointerEvents: 'none',
                    zIndex: 0,
                }}
            >
                {/* Primary animated shapes */}
                <Box
                    sx={{
                        position: 'absolute',
                        width: '400px',
                        height: '400px',
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.08)',
                        top: '-200px',
                        right: '-200px',
                        animation: 'morphFloat 12s ease-in-out infinite',
                        filter: 'blur(1px)',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        width: '300px',
                        height: '300px',
                        borderRadius: '50%',
                        background: 'rgba(255, 255, 255, 0.05)',
                        bottom: '-150px',
                        left: '-150px',
                        animation: 'morphFloat 15s ease-in-out infinite reverse',
                        filter: 'blur(2px)',
                    }}
                />
                
                {/* Floating particles */}
                {particles.map((particle) => (
                    <Box
                        key={particle.id}
                        sx={{
                            position: 'absolute',
                            left: `${particle.x}%`,
                            top: `${particle.y}%`,
                            width: `${particle.size}px`,
                            height: `${particle.size}px`,
                            borderRadius: '50%',
                            background: `rgba(255, 255, 255, ${particle.opacity})`,
                            animation: `particleFloat ${particle.duration}s ease-in-out infinite`,
                            animationDelay: `${particle.delay}s`,
                            filter: 'blur(0.5px)',
                        }}
                    />
                ))}
                
                {/* Magical sparkles */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: '15%',
                        right: '15%',
                        animation: 'sparkle 3s ease-in-out infinite',
                    }}
                >
                    <AutoAwesomeIcon 
                        sx={{ 
                            fontSize: '2rem', 
                            color: 'rgba(255, 255, 255, 0.8)',
                            filter: 'drop-shadow(0 0 10px rgba(255, 255, 255, 0.5))'
                        }} 
                    />
                </Box>
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: '20%',
                        left: '10%',
                        animation: 'sparkle 4s ease-in-out infinite',
                        animationDelay: '1.5s',
                    }}
                >
                    <StarIcon 
                        sx={{ 
                            fontSize: '1.5rem', 
                            color: 'rgba(255, 255, 255, 0.6)',
                            filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.3))'
                        }} 
                    />
                </Box>
            </Box>

            {/* Enhanced Close button */}
            <Fade in={showContent} timeout={1000}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: isMobile ? 20 : 16,
                        right: isMobile ? 20 : 16,
                        zIndex: 10,
                    }}
                >
                    <IconButton
                        onClick={onCancel}
                        sx={{
                            color: '#475569',
                            background: 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(16px) saturate(110%)',
                            WebkitBackdropFilter: 'blur(16px) saturate(110%)',
                            border: '1px solid rgba(226, 232, 240, 0.8)',
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9)',
                            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                background: 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(20px) saturate(115%)',
                                WebkitBackdropFilter: 'blur(20px) saturate(115%)',
                                transform: 'rotate(90deg) scale(1.1)',
                                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 1)',
                                color: '#ef4444',
                            },
                            '&:active': {
                                transform: 'rotate(90deg) scale(0.95)',
                            },
                        }}
                        size={isMobile ? 'medium' : 'small'}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>
            </Fade>

            <DialogContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    padding: isMobile ? 3 : 4,
                    minHeight: isMobile ? '100vh' : '600px',
                    position: 'relative',
                    zIndex: 1,
                    gap: 2,
                }}
            >
                {/* Loading Progress Bar */}
                {loadingProgress < 100 && (
                    <Fade in={showContent} timeout={600}>
                        <Box sx={{ width: '100%', maxWidth: '300px', mb: 2 }}>
                            <LinearProgress 
                                variant="determinate" 
                                value={loadingProgress}
                                sx={{
                                    height: 6,
                                    borderRadius: 3,
                                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                    '& .MuiLinearProgress-bar': {
                                        borderRadius: 3,
                                        background: `linear-gradient(45deg, ${timeInfo.accentColor}, white)`,
                                        boxShadow: '0 0 20px rgba(255, 255, 255, 0.5)',
                                    },
                                }}
                            />
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: 'rgba(255, 255, 255, 0.8)', 
                                    mt: 1,
                                    display: 'block',
                                    textAlign: 'center'
                                }}
                            >
                                Setting up your personalized experience...
                            </Typography>
                        </Box>
                    </Fade>
                )}

                {/* Enhanced Welcome Card */}
                <Grow in={showWelcomeAnimation} timeout={1200}>
                    <Card
                        sx={{
                            maxWidth: isMobile ? '100%' : '480px',
                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 50%, rgba(241, 245, 249, 0.95) 100%)',
                            backdropFilter: 'blur(24px) saturate(110%)',
                            WebkitBackdropFilter: 'blur(24px) saturate(110%)',
                            borderRadius: 6,
                            boxShadow: `
                                0 20px 60px rgba(0, 0, 0, 0.08),
                                0 8px 32px rgba(59, 130, 246, 0.06),
                                inset 0 1px 0 rgba(255, 255, 255, 0.9),
                                0 0 0 1px rgba(226, 232, 240, 0.5)
                            `,
                            border: '1px solid rgba(226, 232, 240, 0.7)',
                            position: 'relative',
                            overflow: 'visible',
                            transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                transform: 'translateY(-8px) scale(1.01)',
                                boxShadow: `
                                    0 24px 80px rgba(0, 0, 0, 0.1),
                                    0 12px 40px rgba(59, 130, 246, 0.08),
                                    inset 0 1px 0 rgba(255, 255, 255, 1),
                                    0 0 0 1px rgba(226, 232, 240, 0.6)
                                `,
                            },
                        }}
                    >
                        {/* Enhanced Decorative Top Bar */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: '8px',
                                background: 'linear-gradient(90deg, #3b82f6 0%, #6366f1 50%, #8b5cf6 100%)',
                                borderTopLeftRadius: 24,
                                borderTopRightRadius: 24,
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    width: '60px',
                                    height: '3px',
                                    background: 'rgba(255, 255, 255, 0.8)',
                                    borderRadius: '0 0 6px 6px',
                                },
                            }}
                        />

                        <CardContent sx={{ padding: isMobile ? 4 : 5, paddingTop: isMobile ? 5 : 6 }}>

                            {/* Status Chip */}
                            <Zoom in={showWelcomeAnimation} timeout={800} style={{ transitionDelay: '300ms' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                                    <Chip
                                        icon={<FavoriteIcon sx={{ color: '#ec4899 !important' }} />}
                                        label="Welcome Back!"
                                        variant="outlined"
                                        sx={{
                                            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(251, 207, 232, 0.4) 100%)',
                                            borderColor: '#f472b6',
                                            color: '#be185d',
                                            fontWeight: 600,
                                            fontSize: '0.85rem',
                                            backdropFilter: 'blur(12px)',
                                            boxShadow: '0 4px 16px rgba(236, 72, 153, 0.1)',
                                        }}
                                    />
                                </Box>
                            </Zoom>

                            {/* Enhanced Greeting Icon */}
                            <Zoom in={showWelcomeAnimation} timeout={1500} style={{ transitionDelay: '400ms' }}>
                                <Box
                                    sx={{
                                        marginBottom: 3,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        position: 'relative',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: isMobile ? '100px' : '120px',
                                            height: isMobile ? '100px' : '120px',
                                            borderRadius: '50%',
                                            background: `linear-gradient(135deg, ${timeInfo.iconColor}20, ${timeInfo.accentColor}20)`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            position: 'relative',
                                            animation: 'iconPulse 3s ease-in-out infinite',
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                inset: -12,
                                                borderRadius: '50%',
                                                background: `linear-gradient(135deg, ${timeInfo.iconColor}10, ${timeInfo.accentColor}10)`,
                                                animation: 'iconPulse 3s ease-in-out infinite reverse',
                                                filter: 'blur(4px)',
                                            },
                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                inset: -6,
                                                borderRadius: '50%',
                                                border: `2px solid ${timeInfo.iconColor}30`,
                                                animation: 'rotateRing 20s linear infinite',
                                            },
                                        }}
                                    >
                                        <GreetingIcon
                                            sx={{
                                                fontSize: isMobile ? '3rem' : '3.5rem',
                                                color: timeInfo.iconColor,
                                                animation: 'iconBounce 4s ease-in-out infinite',
                                                filter: 'drop-shadow(0 6px 12px rgba(0, 0, 0, 0.2))',
                                                position: 'relative',
                                                zIndex: 1,
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Zoom>

                            {/* Enhanced Greeting Messages */}
                            <Fade in={showWelcomeAnimation} timeout={1200} style={{ transitionDelay: '600ms' }}>
                                <Typography
                                    variant={isMobile ? 'h4' : 'h3'}
                                    component="h1"
                                    sx={{
                                        fontWeight: 800,
                                        marginBottom: 1,
                                        background: 'linear-gradient(135deg, #1e40af, #3b82f6, #6366f1, #8b5cf6)',
                                        backgroundSize: '400% 400%',
                                        backgroundClip: 'text',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        letterSpacing: '-1px',
                                        textShadow: 'none',
                                        position: 'relative',
                                        animation: 'gradientShift 4s ease-in-out infinite',
                                    }}
                                >
                                    {timeInfo.greeting}!
                                </Typography>
                            </Fade>

                            <Fade in={showWelcomeAnimation} timeout={1200} style={{ transitionDelay: '800ms' }}>
                                <Box sx={{ marginBottom: 2.5 }}>
                                    <Typography
                                        variant={isMobile ? 'h6' : 'h5'}
                                        sx={{
                                            marginBottom: institutionData?.inst_name ? 1 : 0,
                                            color: timeInfo.accentColor,
                                            fontWeight: 700,
                                            position: 'relative',
                                            display: 'inline-block',
                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                bottom: -6,
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                width: '80%',
                                                height: '3px',
                                                background: `linear-gradient(90deg, transparent, ${timeInfo.iconColor}60, transparent)`,
                                                borderRadius: '3px',
                                                animation: 'underlinePulse 2s ease-in-out infinite',
                                            },
                                        }}
                                    >
                                        {currentUser?.user?.name || 'Dear Student'}
                                    </Typography>
                                    {/* Institution name integrated under user name */}
                                    {institutionData?.inst_name && (
                                        <Typography
                                            variant={isMobile ? 'body2' : 'body1'}
                                            sx={{
                                                color: '#64748b',
                                                fontWeight: 600,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 0.5,
                                                fontSize: isMobile ? '0.85rem' : '0.95rem',
                                                opacity: 0.9,
                                            }}
                                        >
                                            <SchoolIcon sx={{ fontSize: '1rem', color: '#3b82f6' }} />
                                            {institutionData.inst_name}
                                        </Typography>
                                    )}
                                </Box>
                            </Fade>

                            <Slide direction="up" in={showWelcomeAnimation} timeout={1200} style={{ transitionDelay: '1000ms' }}>
                                <Box>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            marginBottom: 4,
                                            color: '#444',
                                            lineHeight: 1.8,
                                            fontSize: isMobile ? '1rem' : '1.1rem',
                                            fontWeight: 500,
                                            textAlign: 'center',
                                        }}
                                    >
                                        {timeInfo.message}
                                    </Typography>

                                    {/* Action Button */}
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={onContinue}
                                        sx={{
                                            background: `linear-gradient(135deg, ${timeInfo.iconColor}, ${timeInfo.accentColor})`,
                                            color: 'white',
                                            fontWeight: 700,
                                            fontSize: '1.1rem',
                                            padding: '16px 32px',
                                            borderRadius: '50px',
                                            textTransform: 'none',
                                            boxShadow: `0 8px 25px ${timeInfo.iconColor}40`,
                                            border: '2px solid transparent',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            '&::before': {
                                                content: '""',
                                                position: 'absolute',
                                                top: 0,
                                                left: '-100%',
                                                width: '100%',
                                                height: '100%',
                                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                                                transition: 'left 0.5s',
                                            },
                                            '&:hover': {
                                                transform: 'translateY(-3px) scale(1.05)',
                                                boxShadow: `0 15px 40px ${timeInfo.iconColor}50`,
                                                '&::before': {
                                                    left: '100%',
                                                },
                                            },
                                            '&:active': {
                                                transform: 'translateY(-1px) scale(1.02)',
                                            },
                                        }}
                                        startIcon={<AutoAwesomeIcon />}
                                    >
                                        Let's Get Started!
                                    </Button>
                                </Box>
                            </Slide>
                        </CardContent>
                    </Card>
                </Grow>

                {/* Enhanced Floating Emojis */}
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        overflow: 'hidden',
                    }}
                >
                    {timeInfo.emojis.map((emoji, index) => (
                        <Fade
                            key={index}
                            in={showWelcomeAnimation}
                            timeout={1500}
                            style={{ transitionDelay: `${1600 + index * 200}ms` }}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    fontSize: isMobile ? '2rem' : '2.5rem',
                                    opacity: 0.8,
                                    animation: `magicalFloat ${8 + index * 2}s ease-in-out infinite`,
                                    animationDelay: `${index * 0.8}s`,
                                    filter: 'drop-shadow(0 0 10px rgba(255, 255, 255, 0.5))',
                                    ...(index === 0 && { top: '8%', left: '8%' }),
                                    ...(index === 1 && { top: '15%', right: '12%' }),
                                    ...(index === 2 && { bottom: '35%', left: '10%' }),
                                    ...(index === 3 && { bottom: '20%', right: '8%' }),
                                    ...(index === 4 && { top: '45%', left: '5%' }),
                                }}
                            >
                                {emoji}
                            </Box>
                        </Fade>
                    ))}
                </Box>
            </DialogContent>

            <style>
                {`
                    @keyframes morphFloat {
                        0%, 100% { 
                            transform: translateY(0px) rotate(0deg) scale(1);
                            border-radius: 50%;
                        }
                        25% { 
                            transform: translateY(-15px) rotate(90deg) scale(1.1);
                            border-radius: 40% 60% 40% 60%;
                        }
                        50% { 
                            transform: translateY(-25px) rotate(180deg) scale(0.9);
                            border-radius: 60% 40% 60% 40%;
                        }
                        75% { 
                            transform: translateY(-15px) rotate(270deg) scale(1.1);
                            border-radius: 50% 50% 70% 30%;
                        }
                    }
                    
                    @keyframes particleFloat {
                        0%, 100% { 
                            transform: translateY(0px) translateX(0px) scale(1);
                            opacity: 0.3;
                        }
                        25% { 
                            transform: translateY(-20px) translateX(10px) scale(1.2);
                            opacity: 0.8;
                        }
                        50% { 
                            transform: translateY(-40px) translateX(-5px) scale(0.8);
                            opacity: 1;
                        }
                        75% { 
                            transform: translateY(-20px) translateX(-10px) scale(1.1);
                            opacity: 0.6;
                        }
                    }
                    
                    @keyframes sparkle {
                        0%, 100% { 
                            opacity: 0.4;
                            transform: scale(1) rotate(0deg);
                        }
                        50% { 
                            opacity: 1;
                            transform: scale(1.3) rotate(180deg);
                        }
                    }
                    
                    @keyframes chipPulse {
                        0%, 100% { transform: scale(1); }
                        50% { transform: scale(1.05); }
                    }
                    
                    @keyframes iconPulse {
                        0%, 100% { 
                            transform: scale(1);
                            box-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
                        }
                        50% { 
                            transform: scale(1.08);
                            box-shadow: 0 0 40px rgba(255, 255, 255, 0.6);
                        }
                    }
                    
                    @keyframes iconBounce {
                        0%, 100% { transform: translateY(0px) rotate(0deg); }
                        25% { transform: translateY(-8px) rotate(5deg); }
                        50% { transform: translateY(-12px) rotate(0deg); }
                        75% { transform: translateY(-8px) rotate(-5deg); }
                    }
                    
                    @keyframes rotateRing {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                    
                    @keyframes textShimmer {
                        0%, 100% { text-shadow: 0 0 20px rgba(0, 0, 0, 0.1); }
                        50% { text-shadow: 0 0 30px rgba(255, 255, 255, 0.3); }
                    }
                    
                    @keyframes underlinePulse {
                        0%, 100% { 
                            width: 80%;
                            opacity: 0.6;
                        }
                        50% { 
                            width: 100%;
                            opacity: 1;
                        }
                    }
                    
                    @keyframes magicalFloat {
                        0%, 100% {
                            transform: translateY(0px) rotate(0deg) scale(1);
                            opacity: 0.6;
                        }
                        25% {
                            transform: translateY(-25px) rotate(90deg) scale(1.2);
                            opacity: 1;
                        }
                        50% {
                            transform: translateY(-35px) rotate(180deg) scale(0.8);
                            opacity: 0.8;
                        }
                        75% {
                            transform: translateY(-20px) rotate(270deg) scale(1.1);
                            opacity: 0.9;
                        }
                    }
                `}
            </style>
        </Dialog>
    );
};

export default InitialWelcomeScreen;